import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Coins, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { tierAiBundle, tierDisplayName, type Tier } from "@/lib/tier";
import { BuyCreditsDialog } from "@/components/settings/BuyCreditsDialog";
import { MonthlyBreakdownCard } from "@/components/settings/credits/MonthlyBreakdownCard";
import { CreditActivityTable } from "@/components/settings/credits/CreditActivityTable";
import { AppSeo } from "@/components/AppSeo";

type CreditStats = {
  tier: Tier;
  monthly_credits_cap: number | null;
  monthly_credits_used: number;
  topup_credits_balance: number;
  byok_active: boolean;
};

const fmt = (n: number) => n.toLocaleString();

const SettingsCredits = () => {
  const { active } = useWorkspace();
  const [buyOpen, setBuyOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  // Deep-link from notification toasts: ?buy_credits=1 auto-opens the dialog.
  useEffect(() => {
    if (params.get("buy_credits") === "1") {
      setBuyOpen(true);
      const next = new URLSearchParams(params);
      next.delete("buy_credits");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const tier = active?.account_tier ?? null;
  const bundle = tierAiBundle(tier);

  const q = useQuery({
    queryKey: ["account-ai-usage", active?.id],
    enabled: !!active?.id,
    queryFn: async (): Promise<CreditStats> => {
      const { data, error } = await supabase.functions.invoke("account-ai-usage", {
        body: { workspace_id: active!.id },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data as CreditStats;
    },
  });

  return (
    <>
    <AppSeo title="Credits" />
    <div className="container mx-auto max-w-5xl space-y-6 px-4 py-6">
      <div className="flex flex-col gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-3 w-fit">
          <Link to="/settings">
            <ArrowLeft className="h-4 w-4" /> Back to settings
          </Link>
        </Button>
        <div className="flex items-center gap-3">
          <Coins className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">AI Credits</h1>
            <p className="text-sm text-muted-foreground">
              Your monthly allowance, top-up balance, and a complete history of
              every credit grant, purchase, and consumption event.
            </p>
          </div>
        </div>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Balance</span>
            {q.data && (
              <span className="text-xs font-normal text-muted-foreground">
                {tierDisplayName(q.data.tier)} plan
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading || bundle === "none" ? (
            <Skeleton className="h-24 w-full" />
          ) : q.isError || !q.data ? (
            <p className="text-xs text-muted-foreground">Couldn't load balance.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-md border border-border/60 bg-muted/30 p-4">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Monthly remaining
                </div>
                <div className="mt-1 text-2xl font-semibold">
                  {q.data.monthly_credits_cap === null
                    ? "—"
                    : fmt(Math.max(0, q.data.monthly_credits_cap - q.data.monthly_credits_used))}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Resets on the 1st
                </div>
              </div>
              <div className="rounded-md border border-border/60 bg-muted/30 p-4">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Top-up balance
                </div>
                <div className="mt-1 text-2xl font-semibold">
                  {fmt(q.data.topup_credits_balance)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Rolls over · never expires
                </div>
              </div>
              <div className="flex items-center justify-center rounded-md border border-dashed border-border p-4">
                <Button onClick={() => setBuyOpen(true)} className="w-full">
                  <Plus className="h-4 w-4" /> Buy credits
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <MonthlyBreakdownCard />
      <CreditActivityTable />

      {active && (
        <BuyCreditsDialog
          open={buyOpen}
          onOpenChange={setBuyOpen}
          workspaceId={active.id}
        />
      )}
    </div>
    </>
  );
};

export default SettingsCredits;
